// AGE-BUG(popovers-do-not-flip) · vendor/age-design/SOURCE_COMMIT 7322a157
//
// AGE anchors the date picker's popover BELOW the trigger, unconditionally, from inside
// its own shadow root:
//
//   mud-date-input::part(picker-popover)  position: absolute; top: 40px
//
// It does not flip, clamp to a viewport edge, or cap its height. Measured on the
// medicine editor: the calendar is 414px tall and hangs 92px below the fold when its
// field sits low on the page — the last week and the "Azi" button are simply
// unreachable without scrolling the page under an open popover.
//
// `mud-select` used to be patched here too. MUD 1.2 flips its listbox above the field
// and caps its height itself (mud-select.tsx, `measureListbox`), so it stopped being a
// target (059 T033). It is one again, but ONLY inside a `mud-modal` (059 V-076): MUD
// measures against the viewport alone, and a dialog's body scrolls inside the
// viewport, so on a 700px-high window the "Motiv" listbox of the stock movement
// dialog was cut after its first option by the body's edge (54px of it hidden) while
// MUD, seeing 170px free below the field in the viewport, decided not to flip. Out of
// a dialog nothing here touches a select and MUD's own placement stands.
//
// `useAnchoredOverlay` in this same library already solves all of it, but only for
// surfaces WE render (RowActionsMenu portals to <body> and is positioned by hand). A
// component that positions itself inside its shadow root cannot be handed to it.
//
// HOW THIS REACHES INSIDE THE SHADOW ROOT
// ---------------------------------------
// The popover is exported as a part, so `::part()` can restyle them — but `::part()`
// takes static declarations and placement is a measurement. The bridge is custom
// properties: they inherit THROUGH the shadow boundary, so the stylesheet reads
// `var(--simsm-pop-*)` and this module writes those variables on the host element. The
// CSS lives in global.css beside the other AGE overrides.
//
// WHAT IT DOES NOT DO
// -------------------
// It does not move the popover horizontally beyond clamping it into the viewport, and it
// does not reposition while the popover is open and the page scrolls under it — AGE
// closes it on scroll, so there is nothing to follow. If that changes, the scroll
// listener belongs here.
//
// The day AGE places this itself, `grep -rn 'AGE-BUG(popovers-do-not-flip)'` finds this
// module, its stylesheet block and its one call site, and all of it can be deleted.

/** The gap AGE itself leaves between a trigger and its popover. */
const OFFSET = 4;

/** Never leave a popover flush against the viewport edge. */
const MARGIN = 8;

/**
 * A popover shorter than this is not worth flipping for: below it, the page can simply
 * be scrolled a little. Above it, a flip that leaves three visible rows is worse than an
 * overflow the reader can scroll to.
 */
const MIN_USEFUL_HEIGHT = 120;

interface Target {
  /** The custom-element tag whose popover this describes. */
  tag: string;
  /** The popover inside its shadow root. */
  selector: string;
  /** The box the popover hangs off, inside the shadow root; the host when absent. */
  anchor?: string;
  /** Only place a host that sits inside this ancestor; every other is left to the component. */
  within?: string;
}

const TARGETS: readonly Target[] = [
  { tag: 'mud-date-input', selector: '.picker-popover' },
  // The anchor is the control row: the host also holds the label and the helper text,
  // and the listbox hangs off the control, not off them.
  { tag: 'mud-select', selector: '[part="listbox"]', anchor: '.control', within: 'mud-modal' },
];

/** Set on a host this module places, so the stylesheet applies its variables to that one only. */
export const PLACED_ATTRIBUTE = 'data-simsm-popover';

export interface PlacementInput {
  /** Viewport-relative top and bottom of the trigger. */
  anchorTop: number;
  anchorBottom: number;
  /** The popover's UNCAPPED height. */
  natural: number;
  viewportHeight: number;
}

export interface Placement {
  side: 'below' | 'above';
  /** `null` when the popover fits and needs no cap. */
  maxHeight: number | null;
}

/**
 * Where a popover of this height goes, given this much room. Pure, and separated from
 * the DOM writing below so the third branch — neither side fits — can be tested without
 * a viewport to shrink.
 *
 * Four outcomes in the order a reader would try them: leave it below when it fits; flip
 * it above when it does not but fits there; otherwise keep the roomier side and cap the
 * height so the popover scrolls inside itself rather than off the screen.
 */
export function decidePlacement(input: PlacementInput): Placement {
  const roomBelow = input.viewportHeight - input.anchorBottom - MARGIN;
  const roomAbove = input.anchorTop - MARGIN;

  if (input.natural <= roomBelow) return { side: 'below', maxHeight: null };
  if (input.natural <= roomAbove) return { side: 'above', maxHeight: null };

  const above = roomAbove > roomBelow;
  return {
    side: above ? 'above' : 'below',
    maxHeight: Math.floor(Math.max(above ? roomAbove : roomBelow, MIN_USEFUL_HEIGHT)),
  };
}

export interface VerticalBox {
  top: number;
  bottom: number;
}

/**
 * The band a popover can actually be seen in: the viewport, cut down by every ancestor
 * that clips its overflow. A field inside a dialog is the case that needed it — the
 * modal's body scrolls, so a popover hanging out of it is clipped at the body's edge
 * long before the viewport's (spec 052: 80px of room below the type select on the
 * correction dialog, one option visible, the rest under the footer).
 */
export function visibleBounds(viewport: VerticalBox, clips: readonly VerticalBox[]): VerticalBox {
  return clips.reduce(
    (box, clip) => ({
      top: Math.max(box.top, clip.top),
      bottom: Math.min(box.bottom, clip.bottom),
    }),
    viewport,
  );
}

// Every ancestor that clips what overflows it, across shadow boundaries: a slotted
// control's box is its slot's in the host's shadow tree, and it is the host's own
// scroll containers (the dialog body) that cut the popover off.
function clippingAncestors(host: HTMLElement): VerticalBox[] {
  const clips: VerticalBox[] = [];
  let node: Element | null = host;
  while (node) {
    const root = node.getRootNode();
    const parent: Element | null =
      node.assignedSlot ?? node.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
    if (!parent || parent === document.documentElement || parent === document.body) break;
    const style = getComputedStyle(parent);
    if (style.overflowY !== 'visible' && style.display !== 'contents') {
      const rect = parent.getBoundingClientRect();
      clips.push({ top: rect.top, bottom: rect.bottom });
    }
    node = parent;
  }
  return clips;
}

/** Decide where one popover goes and write it onto its host. */
function place(host: HTMLElement, popover: Element, target: Target): void {
  const anchorElement = target.anchor ? host.shadowRoot?.querySelector(target.anchor) : null;
  const anchor = (anchorElement ?? host).getBoundingClientRect();
  const box = popover.getBoundingClientRect();
  if (box.height === 0) return;
  host.setAttribute(PLACED_ATTRIBUTE, '');

  const bounds = visibleBounds({ top: 0, bottom: window.innerHeight }, clippingAncestors(host));
  const { side, maxHeight } = decidePlacement({
    // Measured from the top of the visible band, so the pure rule reads its
    // "viewport" as the box the popover is really confined to.
    anchorTop: anchor.top - bounds.top,
    anchorBottom: anchor.bottom - bounds.top,
    // The popover's natural height, not its current one: once this function has capped
    // it the measured height IS the cap, and re-measuring would ratchet it down on every
    // reflow until nothing was left.
    natural: popover.scrollHeight || box.height,
    viewportHeight: bounds.bottom - bounds.top,
  });

  const style = host.style;
  style.setProperty('--simsm-pop-top', side === 'below' ? `calc(100% + ${OFFSET}px)` : 'auto');
  style.setProperty('--simsm-pop-bottom', side === 'above' ? `calc(100% + ${OFFSET}px)` : 'auto');
  if (maxHeight === null) style.removeProperty('--simsm-pop-max-height');
  else style.setProperty('--simsm-pop-max-height', `${maxHeight}px`);

  // Horizontal: only ever pulled back INTO the viewport, never pushed out. A popover
  // wider than its trigger overflows to the right by default, which is correct until it
  // reaches the edge.
  const overflowRight = box.right - (window.innerWidth - MARGIN);
  style.setProperty(
    '--simsm-pop-shift',
    overflowRight > 0 ? `${-Math.ceil(overflowRight)}px` : '0px',
  );
}

/**
 * Watch one host and place its popover whenever it opens.
 *
 * `mud-date-input` does not reflect opening to an attribute, so this watches the shape of
 * the DOM rather than a flag: it ADDS its popover to the shadow root on open and removes
 * it on close. A ResizeObserver catches both that and an element going from
 * `display: none` to rendered, which report a resize, as does one that has just been
 * inserted.
 */
function watch(host: HTMLElement, target: Target): void {
  const root = host.shadowRoot;
  if (!root) return;
  const { selector } = target;

  const resize = new ResizeObserver(() => {
    const popover = root.querySelector(selector);
    if (popover) place(host, popover, target);
  });

  let observed: Element | null = null;
  const attach = (): void => {
    const popover = root.querySelector(selector);
    if (popover === observed) return;
    if (observed) resize.unobserve(observed);
    observed = popover;
    if (popover) {
      resize.observe(popover);
      place(host, popover, target);
    }
  };

  attach();
  // For the date input, whose popover does not exist until it is opened.
  new MutationObserver(attach).observe(root, { childList: true, subtree: true });
}

/**
 * Give every AGE popover in this document a placement that reads the viewport.
 *
 * Called once at boot rather than per component: there are 14 date inputs across the
 * product, in registers, filter bars and forms, and a fix applied at each call site is a
 * fix the next screen forgets. Hosts that appear later — a filter panel opening,
 * a table re-rendering — are picked up by the document observer.
 */
export function installAgePopoverPlacement(): () => void {
  const seen = new WeakSet<HTMLElement>();

  const scan = (): void => {
    for (const target of TARGETS) {
      for (const host of Array.from(document.querySelectorAll<HTMLElement>(target.tag))) {
        // A Stencil component has no shadow root until it hydrates; the observer below
        // brings us back when it does.
        if (seen.has(host) || !host.shadowRoot) continue;
        seen.add(host);
        // Outside the ancestor the target names, the component places itself.
        if (target.within && !host.closest(target.within)) continue;
        watch(host, target);
      }
    }
  };

  scan();
  const observer = new MutationObserver(scan);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}
